import * as XLSX from 'xlsx';
import { calculatePolicyStatus, parseDate, formatDateToDDMMYYYY } from './dateUtils';

/**
 * Format a numeric value for currency fields — returns number or 0.
 */
const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    const num = parseFloat(value);
    return isNaN(num) ? 0 : Math.round(num * 100) / 100;
};

/**
 * Capitalize the first letter of a string.
 */
const capitalize = (str) => {
    if (!str) return '-';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

/**
 * Export policies to an Excel file with two sheets:
 * 1. "Motor Policies" — motor category
 * 2. "Non Motor - Life" — non-motor and life categories
 *
 * @param {Array} policies — array of policy objects (already role-filtered)
 * @returns {{ motorCount: number, nonMotorCount: number }} counts of exported rows
 */
export const exportPoliciesToExcel = (policies) => {
    // 1. Sort policies by expiry date (latest first)
    const sortedPolicies = [...policies].sort((a, b) => {
        const dateA = parseDate(a.expiryDate);
        const dateB = parseDate(b.expiryDate);

        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;

        return dateB.getTime() - dateA.getTime();
    });

    // 2. Separate into motor and non-motor/life, excluding New Leads
    const motorPolicies = [];
    const nonMotorPolicies = [];

    sortedPolicies.forEach((p) => {
        if (p.status === 'New Lead') return;

        if (p.policyCategory === 'motor') {
            motorPolicies.push(p);
        } else {
            // non-motor, life, health, medical — all go to sheet 2
            nonMotorPolicies.push(p);
        }
    });

    // ---- Motor sheet ----
    const motorHeaders = [
        'Customer Name',
        'Customer Mobile',
        'Customer Address',
        'Vehicle Type',
        'Vehicle Number',
        'Make',
        'Model',
        'Policy Type',
        'Company',
        'Policy Number',
        'Expiry Date',
        'Status',
        'Net Premium',
        'Total Premium',
        'Amount Received',
        'Balance Pending',
    ];

    const motorRows = motorPolicies.map((p) => {
        const totalPremium = formatCurrency(p.premiumAmount || p.totalAmount);
        const amountReceived = formatCurrency(p.paidAmount || p.receivedAmount);
        return [
            p.name || '-',
            p.mobile || '-',
            p.address || '-',
            p.vehicleType || '-',
            p.vehicleNumber || '-',
            p.vehicleMake || p.make || '-',
            p.vehicleModel || '-',
            p.policyType || '-',
            p.policyCompany || '-',
            p.policyNumber || '-',
            formatDateToDDMMYYYY(p.expiryDate) || '-',
            calculatePolicyStatus(p.expiryDate, p.status) || '-',
            formatCurrency(p.netPremium),
            totalPremium,
            amountReceived,
            Math.round((totalPremium - amountReceived) * 100) / 100,
        ];
    });

    // ---- Non Motor - Life sheet ----
    const nonMotorHeaders = [
        'Customer Name',
        'Customer Mobile',
        'Customer Address',
        'Category',
        'Policy Type',
        'Company',
        'Policy Number',
        'Expiry Date',
        'Status',
        'Sum Insured',
        'Total Premium',
        'Amount Received',
        'Balance Pending',
    ];

    const nonMotorRows = nonMotorPolicies.map((p) => {
        const totalPremium = formatCurrency(p.premiumAmount || p.totalAmount);
        const amountReceived = formatCurrency(p.paidAmount || p.receivedAmount);
        return [
            p.name || '-',
            p.mobile || '-',
            p.address || '-',
            capitalize(p.policyCategoryType),
            p.policyType || '-',
            p.policyCompany || '-',
            p.policyNumber || '-',
            formatDateToDDMMYYYY(p.expiryDate) || '-',
            calculatePolicyStatus(p.expiryDate, p.status) || '-',
            formatCurrency(p.sumInsured),
            totalPremium,
            amountReceived,
            Math.round((totalPremium - amountReceived) * 100) / 100,
        ];
    });

    // Create workbook
    const wb = XLSX.utils.book_new();

    // Sheet 1: Motor Policies
    const motorData = [motorHeaders, ...motorRows];
    const motorWs = XLSX.utils.aoa_to_sheet(motorData);

    // Auto-size columns based on content width
    motorWs['!cols'] = motorHeaders.map((header, i) => {
        const maxLen = Math.max(
            header.length,
            ...motorRows.map((row) => String(row[i] ?? '').length)
        );
        return { wch: Math.min(maxLen + 2, 40) };
    });

    // Add auto-filter
    if (motorRows.length > 0) {
        motorWs['!autofilter'] = { ref: `A1:${XLSX.utils.encode_col(motorHeaders.length - 1)}${motorRows.length + 1}` };
    }

    XLSX.utils.book_append_sheet(wb, motorWs, 'Motor Policies');

    // Sheet 2: Non Motor - Life
    const nonMotorData = [nonMotorHeaders, ...nonMotorRows];
    const nonMotorWs = XLSX.utils.aoa_to_sheet(nonMotorData);

    nonMotorWs['!cols'] = nonMotorHeaders.map((header, i) => {
        const maxLen = Math.max(
            header.length,
            ...nonMotorRows.map((row) => String(row[i] ?? '').length)
        );
        return { wch: Math.min(maxLen + 2, 40) };
    });

    // Add auto-filter
    if (nonMotorRows.length > 0) {
        nonMotorWs['!autofilter'] = { ref: `A1:${XLSX.utils.encode_col(nonMotorHeaders.length - 1)}${nonMotorRows.length + 1}` };
    }

    XLSX.utils.book_append_sheet(wb, nonMotorWs, 'Non Motor - Life');

    // Generate filename with current date
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    const filename = `policytrackr_policies_${yyyy}-${mm}-${dd}.xlsx`;

    // Trigger download using Blob approach for reliable browser support
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    return { motorCount: motorRows.length, nonMotorCount: nonMotorRows.length };
};
